import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MessageCircle, Check, CornerDownRight, Trash2, RotateCcw, Send, Reply } from "lucide-react";
import { toast } from "sonner";

const initialsOf = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

/**
 * Threaded comment card. `onAction` handles reply/resolve/reopen/delete,
 * all dispatched to the parent so we can revalidate the whole list on each
 * mutation and keep ordering + resolved state consistent.
 */
function CommentCard({ c, children, employees, meId, isAdmin, onAction, isReply = false }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const canDelete = isAdmin || c.user_id === meId;
  const author = employees.find((e) => e.user_id === c.user_id || e.id === c.user_id);
  const name = author?.full_name || c.author_name || "Unknown";

  const submitReply = async () => {
    const text = replyText.trim();
    if (!text) return;
    await onAction("reply", c, { content: text });
    setReplyText(""); setReplyOpen(false);
  };

  return (
    <div className={`space-y-2 ${isReply ? "pl-5 border-l border-border/40" : ""}`} data-testid={`comment-${c.id}`}>
      <div className={`glass rounded-lg p-3 ${c.resolved ? "opacity-60" : ""}`}>
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6"><AvatarFallback className="text-[9px]">{initialsOf(name)}</AvatarFallback></Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate">{name}</div>
            <div className="text-[10px] text-muted-foreground">{timeAgo(c.created_at)}{c.resolved && " · resolved"}</div>
          </div>
          <div className="flex items-center gap-0.5">
            {!c.resolved ? (
              <Button size="icon" variant="ghost" className="h-6 w-6" title="Resolve" onClick={() => onAction("resolve", c)} data-testid={`comment-resolve-${c.id}`}>
                <Check className="w-3.5 h-3.5" />
              </Button>
            ) : (
              <Button size="icon" variant="ghost" className="h-6 w-6" title="Reopen" onClick={() => onAction("reopen", c)}>
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
            )}
            {!isReply && (
              <Button size="icon" variant="ghost" className="h-6 w-6" title="Reply" onClick={() => setReplyOpen((v) => !v)}>
                <Reply className="w-3.5 h-3.5" />
              </Button>
            )}
            {canDelete && (
              <Button size="icon" variant="ghost" className="h-6 w-6" title="Delete" onClick={() => onAction("delete", c)}>
                <Trash2 className="w-3.5 h-3.5 text-red-500" />
              </Button>
            )}
          </div>
        </div>
        <div className="text-sm mt-1.5 whitespace-pre-wrap break-words">{c.content}</div>
      </div>

      {children}

      {replyOpen && (
        <div className="pl-5 space-y-1">
          <Textarea
            rows={2} placeholder="Write a reply…"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitReply(); }}
            data-testid={`reply-input-${c.id}`}
          />
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="outline" onClick={() => setReplyOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={submitReply} data-testid={`reply-submit-${c.id}`}><CornerDownRight className="w-3.5 h-3.5 mr-1" />Reply</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CommentsPanel({ docId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const containerRef = useRef(null);

  const load = async () => {
    try {
      const [c, e] = await Promise.all([
        api.get(`/documents/${docId}/comments`).then((r) => r.data),
        employees.length ? Promise.resolve(employees) : api.get("/employees").then((r) => r.data),
      ]);
      setComments(c); if (!employees.length) setEmployees(e);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [docId]);

  const post = async () => {
    const content = text.trim();
    if (!content) return;
    setPosting(true);
    try {
      await api.post(`/documents/${docId}/comments`, { content });
      setText(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setPosting(false); }
  };

  const handleAction = async (kind, c, extra = {}) => {
    try {
      if (kind === "resolve") await api.post(`/documents/${docId}/comments/${c.id}/resolve`);
      else if (kind === "reopen") await api.post(`/documents/${docId}/comments/${c.id}/reopen`);
      else if (kind === "delete") {
        if (!window.confirm("Delete this comment and any replies?")) return;
        await api.delete(`/documents/${docId}/comments/${c.id}`);
      }
      else if (kind === "reply") {
        await api.post(`/documents/${docId}/comments`, { content: extra.content, parent_comment_id: c.id });
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const topLevel = comments.filter((c) => !c.parent_comment_id);
  const repliesOf = (cid) => comments.filter((c) => c.parent_comment_id === cid);
  const visible = showResolved ? topLevel : topLevel.filter((c) => !c.resolved);
  const openCount = topLevel.filter((c) => !c.resolved).length;

  return (
    <div className="glass rounded-xl p-4 space-y-3" data-testid="comments-panel">
      <div className="flex items-center justify-between">
        <div className="font-medium text-sm flex items-center gap-1.5">
          <MessageCircle className="w-4 h-4" />Comments
          {openCount > 0 && <span className="text-[10px] bg-primary/20 text-primary rounded-full px-1.5 py-0.5">{openCount}</span>}
        </div>
        <button
          onClick={() => setShowResolved((v) => !v)}
          className="text-[10px] text-muted-foreground hover:text-foreground"
        >
          {showResolved ? "Hide resolved" : "Show resolved"}
        </button>
      </div>

      <div ref={containerRef} className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
        {visible.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-6">
            {topLevel.length ? "No open comments." : "Be the first to leave a comment."}
          </div>
        ) : (
          visible.map((c) => (
            <CommentCard
              key={c.id} c={c}
              employees={employees} meId={user?.id} isAdmin={user?.role === "admin"}
              onAction={handleAction}
            >
              {repliesOf(c.id).map((r) => (
                <CommentCard
                  key={r.id} c={r}
                  employees={employees} meId={user?.id} isAdmin={user?.role === "admin"}
                  onAction={handleAction} isReply
                />
              ))}
            </CommentCard>
          ))
        )}
      </div>

      <div className="pt-2 border-t border-border/40 space-y-1">
        <Textarea
          rows={2} placeholder="Comment on this document… (⌘+Enter to send)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) post(); }}
          data-testid="comment-input"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={post} disabled={!text.trim() || posting} data-testid="comment-submit">
            <Send className="w-3.5 h-3.5 mr-1" />{posting ? "Posting…" : "Comment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
